import * as React from "react";
import { useParams } from "react-router";

import { Container, Grid } from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import TranslateIcon from "@mui/icons-material/Translate";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";

import CustomForm from "../../../helper-components/form/CustomForm.jsx";
import PaperFragment from "../../../helper-components/misc/PaperFragment.jsx";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter.jsx";
import PaperText from "../../../helper-components/misc/PaperText.jsx";
import EnhancedAudioPlayer from "./components/EnhancedAudioPlayer.jsx";
import CustomButton from "../../../helper-components/input/CustomButton.jsx";

import { standardFileQuery } from "../../../helper-functions/api/apiUtils.js";
import { isEmpty } from "../../../helper-functions/shared/miscUtils.js";
import { queryAudioTranslation } from "../../../helper-functions/api/translationQueries.js";

import { getLanguage } from "../../../config/language/language.js";
import { API_AUDIO_FILE } from "../../../config/constants/Constants.js";

/**
 * A page detailing a translated audio.
 *
 * Includes:
 * - audio player
 */
export default function AudioTranslationView(props) {
    const { id } = useParams(); // Get the URL parameters from react-router.

    const [audioTranslation, setAudioTranslation] = React.useState({});
    const [audioTranslationQuery, setAudioTranslationQuery] = React.useState(false);

    const audioTranslations = props?.backend?.audioTranslations?.data;

    const isReady = !isEmpty(audioTranslation) && !isEmpty(audioTranslation?.meta_data?.file_name);
    const resource = audioTranslation?.translation?.resource;

    // State to hold the audio blob URL
    const [audioBlobUrl, setAudioBlobUrl] = React.useState(null);

    // Set the audio translation, and potentially query it if it's not available.
    React.useEffect(() => {
        const possibleAudioTranslation = audioTranslations.find((t) => t.id === parseInt(id));

        if (!isEmpty(possibleAudioTranslation)) {
            setAudioTranslationQuery(true);
            setAudioTranslation(possibleAudioTranslation);
        }

        if (isEmpty(possibleAudioTranslation) && !audioTranslationQuery) {
            setAudioTranslationQuery(true);
            queryAudioTranslation(props, { id });
        }
    }, [audioTranslations, audioTranslation]);

    // If the audio translation is ready, fetch the audio file.
    React.useEffect(() => {
        if (!isReady || !audioTranslation?.id) return;

        const path = API_AUDIO_FILE.replace("<audio_id>", audioTranslation?.id);

        standardFileQuery(
            props,
            path,
            (response) => {
                // Create a blob URL for the audio file
                const blob = new Blob([response.body], { type: "audio/mpeg" });
                const url = URL.createObjectURL(blob);
                setAudioBlobUrl(url);
            },
            { headers: { "Content-Type": "audio/mpeg" } }
        );
    }, [isReady, audioTranslation]);

    // Cleanup function for blob URLs
    React.useEffect(() => {
        return () => {
            if (audioBlobUrl) {
                URL.revokeObjectURL(audioBlobUrl);
            }
        };
    }, [audioBlobUrl]);

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm
                        title={getLanguage().AUDIO_TRANSLATION_VIEW_TITLE}
                        subtitle={getLanguage().AUDIO_TRANSLATION_VIEW_SUBTITLE}
                        icon={<TranslateIcon />}
                        form={
                            <CustomButton
                                {...props}
                                title="Open transcript"
                                size="small"
                                path={`/translations/text/${audioTranslation?.translation?.id}`}
                                disabled={!audioTranslation?.translation?.id}
                            />
                        }
                    />
                </Grid>

                {isReady && (
                    <Grid item xs={12}>
                        <PaperText bold center value={resource?.name || "Title: N/A"} />
                    </Grid>
                )}

                {audioTranslationQuery && !isReady && (
                    <Grid item xs={12}>
                        <PaperText value={getLanguage().NO_AUDIO_TRANSLATION_FOUND} />
                    </Grid>
                )}

                {isReady && audioBlobUrl && (
                    <Grid item xs={12}>
                        <PaperFragment
                            p={1.5}
                            value={<EnhancedAudioPlayer {...props} audioSrc={audioBlobUrl} audioType="audio/mpeg" id={`translation_${audioTranslation.id}`} />}
                        />
                    </Grid>
                )}

                <NavigationFooter
                    rows={2}
                    options={[
                        {
                            path: `/translations/audio`,
                            title: "Back to audio translations",
                            variant: "outlined",
                            icon: <AudiotrackIcon />
                        },
                        {
                            path: `/translations/text`,
                            title: "Text translations",
                            variant: "outlined",
                            icon: <TranslateIcon />
                        },
                        {
                            path: `/database/resources`,
                            title: "Resources",
                            variant: "outlined",
                            icon: <DescriptionIcon />
                        }
                    ]}
                />
            </Grid>
        </Container>
    );
}
